import { FC, PropsWithChildren } from 'react'
import { cn } from '../../utils/cn'
import { CustomRadio, RadioRenderProps } from './radio'
import { RadioGroup, RadioGroupProps } from './radio-group'

interface SegmentedGroupOption {
  value: string
  label: string
}

interface SegmentedRadioGroupProps extends RadioGroupProps {
  options: SegmentedGroupOption[]
  radioLabelPrefix: string
}

export const SegmentedRadioGroup: FC<SegmentedRadioGroupProps> = ({
  options,
  radioLabelPrefix,
  ...rest
}) => {
  const { className, ...radioProps } = rest
  const radioGroupClassName = cn(
    'flex w-full rounded-full bg-white p-1',
    className
  )

  return (
    <RadioGroup {...radioProps} className={radioGroupClassName}>
      {options.map((option, idx) => (
        <SegmentedRadio
          key={`segment-group-${option.value}-${idx}`}
          {...option}
          labelPrefix={radioLabelPrefix}
        />
      ))}
    </RadioGroup>
  )
}

const RenderSegmentedRadio: FC<PropsWithChildren<RadioRenderProps>> = ({
  isFocused,
  isSelected,
  children
}) => {
  const className = cn(
    'text-medium flex h-full w-full cursor-pointer items-center justify-center rounded-full text-sm text-slate-950 transition-colors hover:bg-slate-200 hover:text-blue-600',
    {
      'border-blue-600': isFocused || isSelected,
      'bg-blue-600 !text-white hover:bg-blue-600': isSelected,
      'ring-2 ring-blue-600 ring-offset-1': isFocused
    }
  )
  return <div className={className}>{children}</div>
}

interface SegmentedRadioProps {
  value: string
  label: string
  labelPrefix: string
}

const SegmentedRadio: FC<SegmentedRadioProps> = ({
  value,
  label,
  labelPrefix
}) => {
  return (
    <CustomRadio
      label={label}
      value={value}
      aria-label={`${labelPrefix} ${label}`}
      render={(props: RadioRenderProps) => (
        <RenderSegmentedRadio {...props}>{label}</RenderSegmentedRadio>
      )}
      className="block h-8 shrink-0 grow basis-0"
    />
  )
}
